import * as React from 'react';
import { Button } from './button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { Input } from './input';

export type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  confirmVariant?: 'default' | 'destructive';
  onConfirm: () => void | Promise<void>;
  /** Optional inline error message rendered above the action buttons. */
  errorMessage?: string | null;
  /** When true, the confirm button shows a loading state and is disabled. */
  loading?: boolean;
  /** When true, the confirm button is disabled WITHOUT the loading label (e.g. a required field is empty). */
  confirmDisabled?: boolean;
  /** Extra body content rendered between the description and the buttons. */
  children?: React.ReactNode;
  /**
   * When set, the confirm button stays disabled until the user types `expected`
   * (case-sensitive, trimmed). Used to gate irreversible money/ledger actions
   * so they require deliberate intent, not just a click.
   */
  typedConfirm?: {
    expected: string;
    label?: string;
    placeholder?: string;
  };
};

/**
 * Shared confirmation primitive. Wraps the existing Radix Dialog with a
 * standard title/description/footer layout and a destructive button variant.
 *
 * The async `onConfirm` returns a promise — while it's pending the dialog
 * stays open with the confirm button disabled. If it throws, the dialog
 * stays open and `errorMessage` (caller-controlled) surfaces inline.
 * On success the caller is responsible for closing the dialog via
 * `onOpenChange(false)`.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  confirmVariant = 'default',
  onConfirm,
  errorMessage,
  loading = false,
  confirmDisabled = false,
  children,
  typedConfirm,
}: ConfirmDialogProps) {
  const [internalLoading, setInternalLoading] = React.useState(false);
  const [typed, setTyped] = React.useState('');
  const isLoading = loading || internalLoading;
  const typedMatches = !typedConfirm || typed.trim() === typedConfirm.expected;

  React.useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  async function handleConfirm() {
    setInternalLoading(true);
    try {
      await onConfirm();
    } finally {
      setInternalLoading(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (isLoading) return;
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription asChild>
              <div>{description}</div>
            </DialogDescription>
          ) : null}
        </DialogHeader>
        {children}
        {typedConfirm ? (
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">
              {typedConfirm.label ?? `Type "${typedConfirm.expected}" to confirm`}
            </label>
            <Input
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={typedConfirm.placeholder ?? typedConfirm.expected}
              disabled={isLoading}
              aria-label={typedConfirm.label ?? `Type ${typedConfirm.expected} to confirm`}
            />
          </div>
        ) : null}
        {errorMessage ? (
          <p className="text-sm text-destructive" role="alert">
            {errorMessage}
          </p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={confirmVariant}
            onClick={handleConfirm}
            disabled={isLoading || confirmDisabled || !typedMatches}
          >
            {isLoading ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
